import { memo, PropsWithoutRef, useCallback } from 'react'

import { Delete, RotateLeft } from '@mui/icons-material'
import { Box, IconButton, Tooltip } from '@mui/material'
import { SmepEvent } from '@nativewaves/nw-cloud-sdk/smep'
import { Row, Table } from '@tanstack/react-table'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useEventDeleteMutation, useEventWakeMutation } from 'hooks/mutations/smep'
import { useConfirm } from 'hooks/utils'

type SelectionToolbarActionProps = {
  selectedRows: Row<SmepEvent>[]
  tableInstance: Table<SmepEvent>
}

const SelectionToolbarActionsRaw = ({ selectedRows, tableInstance }: PropsWithoutRef<SelectionToolbarActionProps>) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const confirm = useConfirm()

  const { mutateAsync: deleteEvent } = useEventDeleteMutation()
  const { mutate: reprocessEvent } = useEventWakeMutation()

  const count = selectedRows.length

  const handleDeleteSelectedSmepEvents = useCallback(async () => {
    try {
      await confirm(t('domain:Workspace.Event.deleteSelectedEvents', { count }))

      await Promise.allSettled(selectedRows.map((row) => deleteEvent({ eventId: row.original.id })))

      enqueueSnackbar(t('domain:Workspace.Event.eventDeleted', { count }), { variant: 'success' })

      tableInstance.resetRowSelection()
    } catch (_) {
      enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
    }
  }, [confirm, count, deleteEvent, enqueueSnackbar, selectedRows, t, tableInstance])

  const handleReprocessEvent = useCallback(async () => {
    try {
      await Promise.allSettled(selectedRows.map((row) => reprocessEvent({ eventId: row.original.id })))

      enqueueSnackbar(t('domain:Workspace.Event.eventReprocessed', { count }), { variant: 'success' })

      tableInstance.resetRowSelection()
    } catch (_) {
      enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
    }
  }, [count, enqueueSnackbar, reprocessEvent, selectedRows, t, tableInstance])

  return (
    <Box sx={{ ml: 'auto', mr: 2, display: 'flex', alignItems: 'center', columnGap: 1 }}>
      <Tooltip title={t('domain:Workspace.Event.reprocessEvents', { count })} placement="top">
        <IconButton sx={{ ml: 'auto', mr: 0.5, my: 'auto' }} size="medium" onClick={handleReprocessEvent}>
          <RotateLeft fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('domain:Workspace.Event.deleteEvent', { count })} placement="top">
        <IconButton sx={{ mr: 2, my: 'auto' }} size="medium" onClick={handleDeleteSelectedSmepEvents}>
          <Delete fontSize="medium" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export const SelectionToolbarActions = memo(SelectionToolbarActionsRaw)
